import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IngestionJob,
  PricingReport,
  integrationApiService,
} from '../services/integrationApi';
import { RunLogPanel } from './dataIngestion/RunLogPanel';
import {
  chipTone,
  ellipsis,
  ingest,
  primaryButton,
  rowButton,
  secondaryButton,
  tabular,
} from './dataIngestion/ingestionTokens';
import { RunFacts, VERDICT_TONES, runFacts } from './dataIngestion/runFacts';
import { tokens } from '../theme/tokens';

const formatDateTime = (value?: string): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

export const PricingChangesPanel: React.FC<{ jobId: string }> = ({ jobId }) => {
  const { data: report, isLoading } = useQuery<PricingReport | null>({
    queryKey: ['pricing-report', jobId],
    queryFn: () => integrationApiService.fetchJobPricingReport(jobId),
    staleTime: 60_000,
  });

  if (isLoading) return <CircularProgress size={16} />;
  const failedCount = report?.summary?.errored ?? report?.failures?.length ?? 0;
  if (!report || (!report.changes?.length && failedCount === 0)) {
    return <Typography variant="caption" color="text.secondary">No pricing changes</Typography>;
  }

  const formatPrice = (p: number) => (p > 0 ? `$${p.toFixed(2)}` : '—');
  const formatPct = (p: number) => `${p > 0 ? '+' : ''}${p.toFixed(1)}%`;

  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" spacing={1} useFlexGap sx={{ mb: 1, flexWrap: 'wrap' }}>
        <Chip size="small" label={`${report.summary.totalChanged ?? report.changes.length} changed`} color="warning" variant="outlined" />
        {report.summary.fastTracked > 0 && (
          <Chip size="small" label={`${report.summary.fastTracked} auto-applied`} color="success" variant="outlined" />
        )}
        {(report.summary.appliedNeedsReview ?? 0) > 0 && (
          <Chip size="small" label={`${report.summary.appliedNeedsReview} applied, needs review`} color="warning" variant="outlined" />
        )}
        {(report.summary.stagedForReview ?? 0) > 0 && (
          <Chip size="small" label={`${report.summary.stagedForReview} needs review`} color="info" variant="outlined" />
        )}
        {failedCount > 0 && (
          <Chip size="small" label={`${failedCount} failed`} color="error" variant="outlined" />
        )}
        {report.summary.stagedUpdated > 0 && (
          <Chip size="small" label={`${report.summary.stagedUpdated} staged updated`} color="secondary" variant="outlined" />
        )}
      </Stack>
      {report.changes.length > 0 && (
      <TableContainer sx={{ maxHeight: 250, border: `1px solid ${ingest.color.borderInner}`, borderRadius: '7px' }}>
        <Table size="small" stickyHeader sx={{ minWidth: 560 }}>
          <TableHead>
            <TableRow>
              <TableCell>SKU</TableCell>
              <TableCell>Product</TableCell>
              <TableCell align="right">Old price</TableCell>
              <TableCell align="right">New price</TableCell>
              <TableCell align="right">Change</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {report.changes.map((c, i) => (
              <TableRow key={`${c.sku}-${i}`}>
                <TableCell>{c.sku}</TableCell>
                <TableCell sx={{ maxWidth: 220, ...ellipsis }}>{c.name || '—'}</TableCell>
                <TableCell align="right">{formatPrice(c.oldPrice)}</TableCell>
                <TableCell align="right">{formatPrice(c.newPrice)}</TableCell>
                <TableCell align="right">
                  <Typography
                    variant="caption"
                    color={c.newPrice > c.oldPrice ? 'error.main' : 'success.main'}
                  >
                    {formatPct(c.oldPrice > 0 ? ((c.newPrice - c.oldPrice) / c.oldPrice) * 100 : 100)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={String(c.action || '').replace(/_/g, ' ')}
                    color={
                      c.action === 'fast_tracked' ? 'success' :
                      c.action === 'applied_needs_review' ? 'warning' :
                      c.action === 'rejected_suspicious' ? 'error' :
                      c.action === 'staged_for_review' ? 'info' :
                      c.action === 'staged_updated' ? 'secondary' :
                      c.action === 'error' ? 'error' : 'default'
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}
    </Box>
  );
};

interface LogsDialogProps {
  open: boolean;
  job: IngestionJob | null;
  onClose: () => void;
}

const LogsDialog: React.FC<LogsDialogProps> = ({ open, job, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { data: logs, isLoading } = useQuery({
    queryKey: ['ingestion-job-logs', job?.id],
    queryFn: () => (job ? integrationApiService.getIngestionJobLogs(job.id) : Promise.resolve([])),
    enabled: open && !!job,
    refetchInterval: job?.status === 'running' ? 3000 : false,
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
      <DialogTitle>Full log: {job?.brandName}</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ maxHeight: 500, overflow: 'auto', bgcolor: 'grey.900', borderRadius: 1, p: 2 }}>
            {(logs || []).map((log, idx) => (
              <Typography
                key={idx}
                variant="caption"
                component="div"
                sx={{
                  fontFamily: ingest.font.mono,
                  color: log.level === 'error' ? tokens.signal.problem.solid :
                         log.level === 'warn' ? tokens.signal.caution.solid :
                         log.level === 'debug' ? tokens.ink.faint : tokens.ink.inverse,
                }}
              >
                [{formatDateTime(log.timestamp)}] [{log.level.toUpperCase()}] {log.message}
                {log.data && (
                  <Typography component="span" sx={{ ml: 1, opacity: 0.7 }}>
                    {JSON.stringify(log.data)}
                  </Typography>
                )}
              </Typography>
            ))}
            {(!logs || logs.length === 0) && (
              <Typography variant="body2" color="grey.500">No logs available</Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

type JobsFilter = 'all' | 'running' | 'failed' | 'price';

function withinHours(iso?: string, hours = 48): boolean {
  if (!iso) return false;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return false;
  return Date.now() - date.getTime() <= hours * 60 * 60 * 1000;
}

interface IngestionJobsPanelProps {
  embedded?: boolean;
  onTrigger?: () => void;
}

/** Caret, brand/type, status, started, duration, results. */
const COLUMNS = '30px minmax(0, 1.4fr) 112px minmax(0, 1.2fr) 84px minmax(0, 1.3fr)';

const HEADER_CELL = {
  fontFamily: ingest.font.sans,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.07em',
  textTransform: 'uppercase' as const,
  color: ingest.color.faintAlt,
};

export const IngestionJobsPanel: React.FC<IngestionJobsPanelProps> = ({ embedded = false, onTrigger }) => {
  const queryClient = useQueryClient();
  const [selectedJob, setSelectedJob] = useState<IngestionJob | null>(null);
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [filter, setFilter] = useState<JobsFilter>('all');
  const [showAll, setShowAll] = useState(false);
  const [openJobId, setOpenJobId] = useState<string | null>(null);
  const autoOpened = useRef(false);
  const [showPricingFor, setShowPricingFor] = useState<string | null>(null);

  const { data: jobs, isLoading, isError } = useQuery({
    queryKey: ['ingestion-jobs', { limit: showAll ? 200 : 50 }],
    queryFn: () => integrationApiService.getIngestionJobs(undefined, showAll ? 200 : 50),
    refetchInterval: 5000,
  });

  const { data: brands } = useQuery({
    queryKey: ['ingestion-brands'],
    queryFn: () => integrationApiService.getIngestionBrands(),
  });

  const cloudRunAvailable = useMemo(() => brands?.some((b) => b.cloudRunEnabled) ?? false, [brands]);

  const brandLabelById = useMemo(() => {
    const map = new Map<string, string>();
    (brands || []).forEach((b) => map.set(b.id, b.name));
    return map;
  }, [brands]);

  const resolveJobBrandLabel = (job: IngestionJob): string => {
    if (job.brandName?.trim()) return job.brandName.trim();
    return brandLabelById.get(job.brandId) || job.brandId || 'Unknown';
  };

  const windowJobs = useMemo(
    () =>
      showAll
        ? jobs || []
        : (jobs || []).filter((job) => withinHours(job.createdAt || job.startedAt, 48)),
    [jobs, showAll]
  );

  const counts = useMemo(() => ({
    all: windowJobs.length,
    running: windowJobs.filter((job) => job.status === 'running').length,
    failed: windowJobs.filter((job) => job.status === 'failed').length,
    price: windowJobs.filter((job) => job.type === 'price_refresh').length,
  }), [windowJobs]);

  const visibleJobs = windowJobs.filter((job) => {
    if (filter === 'running') return job.status === 'running';
    if (filter === 'failed') return job.status === 'failed';
    if (filter === 'price') return job.type === 'price_refresh';
    return true;
  });

  /** One record per run — the row and its expanded log read the same numbers. */
  const rows: Array<{ job: IngestionJob; facts: RunFacts; brandLabel: string }> = visibleJobs.map((job) => ({
    job,
    facts: runFacts(job),
    brandLabel: resolveJobBrandLabel(job),
  }));

  // The newest run is the one an operator opens the tab to read, so it starts expanded — once.
  useEffect(() => {
    if (autoOpened.current) return;
    if (rows.length === 0) return;
    autoOpened.current = true;
    setOpenJobId(rows[0].job.id);
  }, [rows]);

  const cancelMutation = useMutation({
    mutationFn: (jobId: string) => integrationApiService.cancelIngestionJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs'] });
    },
  });

  const filterChips: Array<{ id: JobsFilter; label: string; count: number }> = [
    { id: 'all', label: 'All', count: counts.all },
    { id: 'running', label: 'Running', count: counts.running },
    { id: 'failed', label: 'Failed', count: counts.failed },
    { id: 'price', label: 'Price', count: counts.price },
  ];

  return (
    <Box
      sx={{
        border: `1px solid ${ingest.color.border}`,
        borderRadius: '10px',
        background: ingest.color.surface,
        boxShadow: ingest.shadow.card,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '14px',
          flexWrap: 'wrap',
          borderBottom: `1px solid ${ingest.color.borderInner}`,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
            <Box sx={{ fontFamily: ingest.font.sans, fontSize: '15px', fontWeight: 600, color: ingest.color.ink }}>
              Ingestion jobs
            </Box>
            <Box
              component="span"
              sx={{
                height: 20,
                padding: '0 8px',
                borderRadius: '10px',
                background: cloudRunAvailable ? ingest.color.successTint : tokens.signal.neutral.tint,
                color: cloudRunAvailable ? ingest.color.successInk : ingest.color.muted,
                fontFamily: ingest.font.sans,
                fontSize: '11px',
                fontWeight: 500,
                lineHeight: '20px',
              }}
            >
              {cloudRunAvailable ? 'Cloud Run' : 'Local'}
            </Box>
          </Box>
          <Box sx={{ fontFamily: ingest.font.sans, fontSize: '12.5px', color: ingest.color.faint, marginTop: '3px' }}>
            {showAll
              ? 'Every run across all connectors, newest first. Expand a row for its log.'
              : 'Every run across all connectors in the last 48 hours, newest first. Expand a row for its log.'}
          </Box>
        </Box>
        <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {filterChips.map((chip) => {
            const tone = chipTone(filter === chip.id);
            return (
              <Box
                key={chip.id}
                component="button"
                type="button"
                aria-pressed={filter === chip.id}
                onClick={() => setFilter(chip.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  height: 30,
                  padding: '0 11px',
                  border: `1px solid ${tone.border}`,
                  background: tone.bg,
                  borderRadius: '15px',
                  fontFamily: ingest.font.sans,
                  fontSize: '12px',
                  fontWeight: tone.weight,
                  color: tone.ink,
                  cursor: 'pointer',
                  '&:hover': { borderColor: filter === chip.id ? tone.border : tokens.ink.disabled },
                }}
              >
                <span>{chip.label}</span>
                <Box component="span" sx={{ ...tabular, color: tone.countInk }}>
                  {chip.count}
                </Box>
              </Box>
            );
          })}
          <Button disableElevation sx={secondaryButton} onClick={() => setShowAll((prev) => !prev)}>
            {showAll ? 'Last 48 hours' : 'Show all'}
          </Button>
          {!embedded && onTrigger && (
            <Button disableElevation sx={primaryButton} onClick={onTrigger}>
              ▶ Trigger ingestion
            </Button>
          )}
        </Box>
      </Box>

      {isError && <Alert severity="error" sx={{ margin: '14px 20px' }}>Failed to load ingestion jobs</Alert>}

      <Box sx={{ overflowX: { xs: 'visible', md: 'auto' } }}>
        <Box role="grid" aria-label="Ingestion runs" sx={{ minWidth: { xs: 0, md: 880 } }}>
          <Box
            role="row"
            sx={{
              display: { xs: 'none', md: 'grid' },
              gridTemplateColumns: { xs: '1fr', md: COLUMNS },
              gap: '12px',
              alignItems: 'center',
              padding: '12px 20px 8px',
              borderBottom: `1px solid ${ingest.color.borderInner}`,
              ...HEADER_CELL,
            }}
          >
            <Box role="columnheader" aria-label="Expand" />
            <Box role="columnheader">Brand / type</Box>
            <Box role="columnheader">Status</Box>
            <Box role="columnheader">Started</Box>
            <Box role="columnheader">Duration</Box>
            <Box role="columnheader">Results</Box>
          </Box>

          {isLoading && (
            <Box sx={{ padding: '24px 20px', display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={22} />
            </Box>
          )}

          {!isLoading && rows.length === 0 && (
            <Box
              sx={{ padding: '20px', fontFamily: ingest.font.sans, fontSize: '12.5px', color: ingest.color.faintAlt }}
            >
              {showAll
                ? 'No ingestion jobs in this filter.'
                : 'No ingestion jobs in this filter for the last 48 hours.'}
            </Box>
          )}

          {rows.map(({ job, facts, brandLabel }) => {
            const open = openJobId === job.id;
            const verdictTone = VERDICT_TONES[facts.verdict];
            const cancellable = job.status === 'running' || job.status === 'pending';
            const priceChanges =
              (job.result?.pricingReport?.totalChanged ?? 0) +
              (job.result?.pricingReport?.errored ?? 0);

            return (
              <Box key={job.id}>
                <Box
                  role="row"
                  tabIndex={0}
                  aria-expanded={open}
                  aria-label={`${brandLabel}, ${facts.kind}. ${facts.verdict}. ${facts.result}.`}
                  onClick={() => setOpenJobId(open ? null : job.id)}
                  onKeyDown={(event: React.KeyboardEvent) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    setOpenJobId(open ? null : job.id);
                  }}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'minmax(0,1fr)', md: COLUMNS },
                    gap: { xs: '6px', md: '12px' },
                    alignItems: { xs: 'stretch', md: 'center' },
                    width: '100%',
                    padding: '10px 20px',
                    border: 0,
                    borderBottom: `1px solid ${ingest.color.borderFaintAlt}`,
                    background: open ? ingest.color.rowOpen : ingest.color.surface,
                    cursor: 'pointer',
                    textAlign: 'left',
                    '&:hover': { background: open ? ingest.color.rowOpen : ingest.color.sunken },
                    '&:focus-visible': { outline: `2px solid ${ingest.color.accent}`, outlineOffset: '-2px' },
                  }}
                >
                  <Box
                    component="span"
                    role="gridcell"
                    aria-hidden
                    sx={{ fontSize: '11px', color: ingest.color.faintAlt, textAlign: 'center' }}
                  >
                    {open ? '▾' : '▸'}
                  </Box>
                  <Box component="span" role="gridcell" sx={{ minWidth: 0 }}>
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        fontFamily: ingest.font.sans,
                        fontSize: '13px',
                        fontWeight: 500,
                        color: ingest.color.ink,
                        ...ellipsis,
                      }}
                    >
                      {brandLabel}
                      {job.config?.region === 'CA' ? ' · CA' : ''}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        fontFamily: ingest.font.sans,
                        fontSize: '11px',
                        color: ingest.color.faintAlt,
                        marginTop: '1px',
                      }}
                    >
                      {facts.kind}
                    </Box>
                  </Box>
                  <Box component="span" role="gridcell" sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        height: 21,
                        padding: '0 9px',
                        borderRadius: '11px',
                        background: verdictTone.bg,
                        color: verdictTone.ink,
                        fontFamily: ingest.font.sans,
                        fontSize: '11.5px',
                        fontWeight: 500,
                        lineHeight: '21px',
                      }}
                    >
                      {facts.verdict}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        display: { xs: 'inline', md: 'none' },
                        fontFamily: ingest.font.sans,
                        fontSize: '11.5px',
                        color: ingest.color.faintAlt,
                        ...tabular,
                      }}
                    >
                      {formatDateTime(job.startedAt || job.createdAt)} · {facts.duration}
                    </Box>
                  </Box>
                  <Box
                    component="span"
                    role="gridcell"
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      fontFamily: ingest.font.sans,
                      fontSize: '12.5px',
                      color: ingest.color.muted,
                      ...tabular,
                      ...ellipsis,
                    }}
                  >
                    {formatDateTime(job.startedAt || job.createdAt)}
                  </Box>
                  <Box
                    component="span"
                    role="gridcell"
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      fontFamily: ingest.font.sans,
                      fontSize: '12.5px',
                      color: ingest.color.body,
                      ...tabular,
                    }}
                  >
                    {facts.duration}
                  </Box>
                  <Box component="span" role="gridcell" sx={{ minWidth: 0 }}>
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        fontFamily: ingest.font.sans,
                        fontSize: '12.5px',
                        color: ingest.color.body,
                        ...tabular,
                        ...ellipsis,
                      }}
                    >
                      {facts.result}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        fontFamily: ingest.font.sans,
                        fontSize: '11px',
                        color: facts.partialFailure ? ingest.color.warningInk : ingest.color.faintAlt,
                        marginTop: '1px',
                        ...tabular,
                        ...ellipsis,
                      }}
                    >
                      {facts.verdict === 'Failed' && facts.error
                        ? facts.error
                        : facts.partialFailure
                          ? `Partial failure · ${facts.partialFailure}`
                          : `${facts.created} new · ${facts.updated} updated`}
                    </Box>
                  </Box>
                </Box>

                {open && (
                  <RunLogPanel logs={facts.logs} actor={facts.actor} maxHeight={220} padding="12px 20px 16px">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                      <Button
                        disableElevation
                        sx={rowButton}
                        onClick={() => {
                          setSelectedJob(job);
                          setLogsDialogOpen(true);
                        }}
                      >
                        Full log
                      </Button>
                      {priceChanges > 0 && (
                        <Button
                          disableElevation
                          sx={rowButton}
                          onClick={() => setShowPricingFor(showPricingFor === job.id ? null : job.id)}
                        >
                          {showPricingFor === job.id ? 'Hide' : 'Pricing changes'} ({priceChanges})
                        </Button>
                      )}
                      {cancellable && (
                        <Button
                          disableElevation
                          onClick={() => cancelMutation.mutate(job.id)}
                          disabled={cancelMutation.isPending}
                          sx={{
                            ...rowButton,
                            borderColor: ingest.color.dangerControl,
                            color: ingest.color.dangerInk,
                            '&:hover': {
                              background: ingest.color.surface,
                              borderColor: ingest.color.dangerGlyph,
                              color: ingest.color.dangerInk,
                            },
                          }}
                        >
                          Cancel run
                        </Button>
                      )}
                    </Box>
                    <Collapse in={showPricingFor === job.id} timeout="auto" unmountOnExit>
                      <PricingChangesPanel jobId={job.id} />
                    </Collapse>
                  </RunLogPanel>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ padding: '12px 20px', fontFamily: ingest.font.sans, fontSize: '12px', color: ingest.color.faint }}>
        Showing {rows.length} of {windowJobs.length} runs{showAll ? '' : ' from the last 48 hours'}
      </Box>

      <LogsDialog open={logsDialogOpen} job={selectedJob} onClose={() => setLogsDialogOpen(false)} />
    </Box>
  );
};

export default IngestionJobsPanel;
